/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, CSSProperties, ChangeEvent, useCallback, MouseEventHandler, useEffect } from 'react';
import { Popover, Tabs, Typography, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getValidIconNames } from '../../../../components/dynamicIcon/dynamicIcon';
import { WIDGET_DEFAULT_ICON_NAME, WIDGET_ICON_MAX_FILE_SIZE } from '../../../../common/constants';
import StyledInput from '../../../../components/form-control/styledInput/styledInput';
import useTranslation from '../../../../common/hooks/useTranslation';
import styles from './iconSelector.module.css';
import useKemuNotification from '@hooks/useKemuNotification';
import SvgFromString from '@components/SvgFromString/SvgFromString';
import { isSvgString, addWhiteFillToSvg, processSvgFile, SvgErrorKey } from '@components/gates/common';
import FileDropZone from '@components/FileDropZone/FileDropZone';

const { Text } = Typography;

interface Props {
	icon?: string;
	backgroundColor?: string;
	onIconSelected?: (icon: string) => void;
	onCustomSvgAdded?: (svgString: string) => void;
}

const iconList = getValidIconNames();

const IconSelector = (props: Props): React.JSX.Element => {
	const { onIconSelected, onCustomSvgAdded } = props;
	const currentIcon = props.icon;
	const styledBody: CSSProperties = props.backgroundColor ? { backgroundColor: props.backgroundColor } : {};
	const [filter, setFilter] = useState('');
	const [filteredList, setFilteredList] = useState<string[]>(iconList);
	const [activeTab, setActiveTab] = useState<string>('icons');
	const [customSvgString, setCustomSvgString] = useState<string>('');
	const [fileLoaded, setFileLoaded] = useState(false);
	const t = useTranslation('IconSelector.SearchInput');
	const cw = useTranslation('CustomWidgetModal');
	const svgValidation = useTranslation('SvgValidations');
	const kemuNotification = useKemuNotification();

	const onFilterChange = (event: ChangeEvent<HTMLInputElement>) => {
		const text = event.currentTarget.value;
		setFilter(text);
		setFilteredList(iconList.filter(name => name.includes(text)));
	};

	const handleSelectedIcon: MouseEventHandler<HTMLDivElement> = useCallback((event) => {
		const name = event.currentTarget.getAttribute('data-name') || '';
		if (name !== '') {
			onIconSelected?.(name);
		}
	}, [onIconSelected]);

	/**
	 * Handles file added from FileDropZone
	 * Validates file size, SVG content, and sanitizes the SVG before storing
	 */
	const handleFileAdded = useCallback((data: Uint8Array) => {
		const sanitizedSvg = processSvgFile(
			data,
			WIDGET_ICON_MAX_FILE_SIZE,
			(errorKey) => {
				kemuNotification({
					code: 'widget-icon-error',
					message: svgValidation(
						errorKey,
						errorKey === SvgErrorKey.FILE_SIZE
							? { maxSize: WIDGET_ICON_MAX_FILE_SIZE / 1024 }
							: undefined
					),
					type: 'error',
				});
			}
		);

		if (!sanitizedSvg) {
			return;
		}

		// Store sanitized SVG
		setCustomSvgString(sanitizedSvg);
		setFileLoaded(true);
		onCustomSvgAdded?.(sanitizedSvg);
	}, [onCustomSvgAdded, svgValidation, kemuNotification]);

	// Check if current icon is a custom SVG
	const isCustomSvg = isSvgString(currentIcon);

	// Update state when icon prop changes externally
	useEffect(() => {
		if (isCustomSvg) {
			setCustomSvgString(currentIcon || '');
			setFileLoaded(true);
			setActiveTab('custom');
		} else {
			setCustomSvgString('');
			setFileLoaded(false);
			setActiveTab('icons');
		}
	}, [currentIcon, isCustomSvg]);

	return (
		<Popover
			placement="rightTop"
			mouseLeaveDelay={0.1}
			trigger="click"
			overlayClassName={styles.PopupOverlay}
			content={
				<Tabs
					activeKey={activeTab}
					onChange={setActiveTab}
					items={[
						{
							key: 'icons',
							label: cw('Tabs.Icons'),
							children: (
								<div>
									<div className={styles.SearchBox}>
										<StyledInput
											placeholder={t('Placeholder', 'Filter icons')}
											className={styles.SearchInput}
											value={filter}
											onChange={onFilterChange}
										/>
										<span>{filteredList.length} {t('Icons', 'icons')}</span>
									</div>
									<div className={styles.IconsGrid}>
										{filteredList.map((iconName) => {
											return (
												<div key={iconName} data-name={iconName} className={styles.GridIcon} onClick={handleSelectedIcon} >
													<FontAwesomeIcon icon={['fas', iconName as any]} />
												</div>
											);
										})}
									</div>
								</div>
							),
						},
						{
							key: 'custom',
							label: cw('Tabs.Custom'),
							children: (
								<div className={styles.CustomSvgTab}>
									<FileDropZone
										onFileAdded={handleFileAdded}
										accept=".svg"
										fileLoaded={fileLoaded}
										className={styles.FileDropZone}
										hoverClass={styles.FileDropZoneHover}
										noFileElement={
											<div className={styles.DropZoneContent}>
												<UploadOutlined className={styles.UploadIcon} />
												<Text className={styles.DropText}>{cw('DragDropText')}</Text>
												<Text type="secondary" className={styles.OrText}>{cw('OrText')}</Text>
												<Button type="dashed" size="small">
													{cw('ChooseFileBtn')}
												</Button>
											</div>
										}
										yesFileElement={
											<div className={styles.FileLoadedContent}>
												<SvgFromString
													svgStr={customSvgString}
													width={48}
													height={48}
												/>
												<Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
													{cw('ClickToChange')}
												</Text>
											</div>
										}
									/>
								</div>
							),
						},
					]}
				/>
			}
		>
		<div className={styles.PreviewContainer} style={styledBody}>
			{isCustomSvg ? (
				<SvgFromString
					svgStr={addWhiteFillToSvg(currentIcon || '')}
					className={styles.PreviewSvg}
					width={20}
					height={20}
				/>
			) : currentIcon !== undefined ? (
				<FontAwesomeIcon icon={['fas', currentIcon as any]} />
			) : (
				<FontAwesomeIcon icon={['fas', WIDGET_DEFAULT_ICON_NAME]} />
			)}
		</div>
		</Popover>
	);
};

export default IconSelector;
